import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from "recharts";
import { MotivationalProfile } from "@/components/MotivationalProfile";
import { InterviewPanel } from "@/components/InterviewPanel";
import { RottaPanel } from "@/components/RottaPanel";
import { toast } from "sonner";
import { ArrowLeft, Download, ExternalLink, FileText, ScrollText, AlertTriangle, HelpCircle, Trash2, Check, X, Minus } from "lucide-react";

const RADAR_ANGLE_TICK = { fill: "#475569", fontSize: 10 };
const RADAR_RADIUS_TICK = { fontSize: 9 };
const RADAR_DOMAIN = [0, 5];

const DOC_LABELS = {
  titolo_autodichiarazione: "Autodichiarazione titolo di studio",
  titolo_diploma: "Diploma",
  titolo_dichiarazione_valore: "Dichiarazione di valore",
  carta_identita: "Carta d'identità",
  codice_fiscale_doc: "Copia codice fiscale",
  curriculum: "Curriculum vitae",
  lettera_motivazionale: "Lettera motivazionale",
  cert_inglese_b1: "Certificazione inglese (B1 o superiore)",
  cert_ecdl: "Certificazione ECDL/ICDL",
};

const ANAG_LABELS = {
  nome: "Nome", cognome: "Cognome", data_nascita: "Data di nascita", luogo_nascita: "Luogo di nascita", codice_fiscale: "Codice fiscale",
  nazionalita: "Nazionalità", indirizzo: "Indirizzo", numero_civico: "Numero civico",
  comune: "Comune", provincia: "Provincia", cap: "CAP", telefono: "Telefono",
  permesso_soggiorno: "Permesso di soggiorno",
};

const PROFILE_LABELS = {
  solido: { label: "Solido", cls: "bg-green-100 text-green-800" },
  da_verificare: { label: "Da verificare", cls: "bg-amber-100 text-amber-800" },
  esplorativo: { label: "Esplorativo", cls: "bg-blue-100 text-blue-800" },
  poco_compatibile: { label: "Poco compatibile", cls: "bg-red-100 text-red-800" },
  non_interpretabile: { label: "Non interpretabile", cls: "bg-slate-200 text-slate-700" },
};

const ALERT_LABELS = {
  quasi_tutti_5: "Quasi tutte risposte pari a 5",
  incoerenza_diretti_invertiti: "Incoerenza tra item diretti e invertiti",
  desiderabilita_sociale_alta: "Desiderabilità sociale alta",
  variabilita_bassa: "Variabilità bassa delle risposte",
};

const PREF_LABELS = ["1ª preferenza", "2ª preferenza", "3ª preferenza"];

function docUrl(doc) {
  return `${API}/files/${doc.storage_path}`;
}

function AnswerIcon({ selected, correct }) {
  if (selected === correct) return <Check className="h-4 w-4 text-green-600 shrink-0" />;
  if (selected == null) return <Minus className="h-4 w-4 text-slate-400 shrink-0" />;
  return <X className="h-4 w-4 text-red-500 shrink-0" />;
}

export default function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = () => api.get(`/admin/applications/${id}`).then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="candidate-detail-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-[#003366]" />
      </div>
    );
  }

  const sess = data.test_session;
  const sectionB = sess?.section_b;
  const prof = sectionB?.profile ? PROFILE_LABELS[sectionB.profile] : null;
  const radarData = (sess?.radar || []).map((r) => ({ subject: r.label.replace("Controllo acquiescenza (invertiti)", "E · Invertiti"), score: r.score }));
  const isNewInstrument = sess?.instrument_version === "itsmatch-orientativo-2.1.0";
  const isRotta = sess?.instrument_version === "itsmatch-rotta-4.0.0";
  const adminProfile = isNewInstrument && sectionB ? {
    areas: (sess.display_order || ["C", "A", "D", "B"]).map((aid) => ({
      area: aid,
      label: sess.area_meta?.[aid]?.label || aid,
      description: sess.area_meta?.[aid]?.description || "",
      mean: sectionB.areas?.[aid]?.mean,
      mean_display: sectionB.areas?.[aid]?.mean_display,
      answered: sectionB.areas?.[aid]?.answered ?? 0,
      complete: !!sectionB.areas?.[aid]?.complete,
    })),
    answers: sess.section_b_answers || [],
    coverage: sectionB.coverage,
  } : null;
  const oa = sess?.open_answers || {};
  const osugg = sess?.open_suggestions || {};

  const doDelete = async () => {
    try {
      await api.delete(`/admin/applications/${id}`);
      toast.success("Candidatura eliminata");
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-candidate-detail">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button data-testid="detail-back-button" variant="ghost" size="sm" onClick={() => navigate("/admin")}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Torna alle candidature
          </Button>
          <Button data-testid="detail-export-button" variant="outline" size="sm" asChild>
            <a href={`${API}/admin/export/candidatura/${id}.csv`} download>
              <Download className="h-4 w-4 mr-2" /> Esporta candidatura
            </a>
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="detail-delete-button" variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="h-4 w-4 mr-2" /> Elimina candidatura
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="detail-delete-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Elimina candidatura</AlertDialogTitle>
                <AlertDialogDescription>
                  Sei sicuro di voler eliminare questa candidatura? L'operazione è irreversibile.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="detail-delete-cancel">Annulla</AlertDialogCancel>
                <AlertDialogAction data-testid="detail-delete-confirm" className="bg-red-600 hover:bg-red-700" onClick={doDelete}>
                  Elimina definitivamente
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="eyebrow">Candidatura · {data.tenant_name}</p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              {data.anagrafica?.nome} {data.anagrafica?.cognome}
            </h1>
            <p className="text-sm text-slate-600 mt-1">{data.course_name} · {data.user?.email}</p>
          </div>
          <div className="flex gap-2">
            {data.anagrafica?.extra_ue && <Badge className="bg-purple-100 text-purple-800">Cittadino extra-UE</Badge>}
            {data.anagrafica?.dsa_declared && <Badge data-testid="detail-dsa-badge" className="bg-teal-100 text-teal-800">DSA dichiarato (+30% di tempo)</Badge>}
            <Badge className="bg-blue-100 text-blue-800">{data.status}</Badge>
          </div>
        </div>

        {(data.course_preferences || []).length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="detail-preferences">
            {data.course_preferences.map((p) => (
              <Badge key={p.rank} variant="secondary" className="text-xs">
                {PREF_LABELS[p.rank - 1] || `${p.rank}ª`}: {p.course_name}
              </Badge>
            ))}
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="institutional-card">
            <CardContent className="p-6">
              <p className="eyebrow mb-4">Dati anagrafici</p>
              <dl className="space-y-2 text-sm" data-testid="detail-anagrafica">
                {Object.entries(ANAG_LABELS).map(([k, label]) => (
                  <div key={k} className="flex justify-between gap-4">
                    <dt className="text-slate-500">{label}</dt>
                    <dd className="font-medium text-slate-900 text-right">{data.anagrafica?.[k] || "—"}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>

          <Card className="institutional-card">
            <CardContent className="p-6">
              <p className="eyebrow mb-4">Documenti</p>
              <ul className="space-y-3" data-testid="detail-documents">
                {Object.entries(data.documents || {}).map(([k, d]) => (
                  <li key={k} className="flex flex-wrap items-center justify-between gap-2 text-sm border border-slate-100 rounded-lg p-3">
                    <span className="flex flex-wrap items-center gap-2 text-slate-700 min-w-0">
                      <FileText className="h-4 w-4 text-[#0284C7]" /> {DOC_LABELS[k] || k}
                      {d.storage === "drive" && <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Drive</Badge>}
                      {d.drive_fallback && <Badge className="bg-amber-100 text-amber-800 text-[10px]">Drive fallito → storage interno</Badge>}
                    </span>
                    {d.storage === "drive" ? (
                      <a data-testid={`doc-open-drive-${k}`} href={`https://drive.google.com/file/d/${d.drive_file_id}/view`} target="_blank" rel="noreferrer"
                        className="text-[#003366] hover:underline flex items-center gap-1 text-xs font-medium">
                        <ExternalLink className="h-3.5 w-3.5" /> Apri su Drive
                      </a>
                    ) : (
                      <a data-testid={`doc-download-${k}`} href={docUrl(d)} download={d.filename || true} target="_blank" rel="noreferrer"
                        className="text-[#003366] hover:underline flex items-center gap-1 text-xs font-medium">
                        <Download className="h-3.5 w-3.5" /> Scarica
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {sess && (
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="institutional-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="eyebrow">Sezione A · Risposta chiusa</p>
                    <Badge className={sess.status === "completed" ? "bg-green-100 text-green-800" : sess.status === "terminated" ? "bg-red-100 text-red-800" : sess.status === "screened_out" ? "bg-slate-200 text-slate-700" : "bg-amber-100 text-amber-800"}>
                      {sess.status === "completed" ? "Completato" : sess.status === "terminated" ? "Annullato" : sess.status === "screened_out" ? "Sotto sbarramento" : "In corso"}
                    </Badge>
                  </div>
                  <div data-testid="admin-section-a-total" className="mt-4 font-display text-4xl font-extrabold text-[#003366]">
                    {sess.section_a_total ?? "—"}<span className="text-lg text-slate-400">/{sess.section_a_max ?? "—"}</span>
                  </div>
                  {sess.sbarramento && (
                    <p className="mt-2 text-xs text-slate-500" data-testid="admin-sbarramento-info">
                      Sbarramento: minimo {sess.sbarramento.min_score} punti — {sess.sbarramento.passed ? "superato" : "NON superato"}
                    </p>
                  )}
                  <div className="mt-5 space-y-2" data-testid="admin-section-a-scores">
                    {Object.entries(sess.section_a_scores || {}).map(([cat, s]) => (
                      <div key={cat} className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">{cat}</span>
                        <span className="flex items-center gap-2">
                          {s.skipped && <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Esente (certificato B1 o superiore)</Badge>}
                          <Badge variant="secondary">{s.earned}/{s.max} pt</Badge>
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="institutional-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between gap-2">
                    <p className="eyebrow">Sezione B · {isRotta ? "La rotta ITS" : isNewInstrument ? "Questionario orientativo" : "Scheda orientativa"}</p>
                    {!isNewInstrument && !isRotta && sectionB && <Badge variant="secondary" className="text-[10px]" data-testid="legacy-version-badge">Versione storica</Badge>}
                  </div>
                  {isRotta && sectionB ? (
                    <div className="mt-4" data-testid="admin-section-b">
                      <p className="text-xs text-slate-500">
                        Strumento <span className="font-mono2">{sectionB.instrument_version}</span> · regole{" "}
                        <span className="font-mono2">{sectionB.rule_version}</span> — sintesi editoriale a supporto
                        del colloquio, nessun punteggio automatico in graduatoria.
                      </p>
                    </div>
                  ) : isNewInstrument && sectionB ? (
                    <div className="mt-4" data-testid="admin-section-b">
                      <p className="text-xs text-slate-500">
                        Strumento <span className="font-mono2">{sectionB.instrument_version}</span> — autovalutazione
                        orientativa, nessun punteggio automatico in graduatoria.
                      </p>
                      <p className="mt-2 text-sm text-slate-700" data-testid="admin-coverage">
                        Questionario principale {sectionB.coverage?.main_answered ?? 0}/{sectionB.coverage?.main_total ?? 24} ·
                        spunti aggiuntivi {sectionB.coverage?.qualitative_answered ?? 0}/{sectionB.coverage?.qualitative_total ?? 3}
                      </p>
                      {sectionB.uniform_pattern && (
                        <p className="mt-2 text-xs text-slate-500" data-testid="uniform-pattern-note">
                          La stessa risposta è stata selezionata in tutti i 24 item principali.
                        </p>
                      )}
                    </div>
                  ) : sectionB ? (
                    <div className="mt-4" data-testid="admin-section-b-legacy">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="font-display text-4xl font-extrabold text-[#003366]" data-testid="admin-its-index">
                          {sectionB.its_index.toFixed(2)}
                        </div>
                        <div>
                          <div className="text-xs text-slate-500">Indice ITS (soglie 2.61 / 3.41)</div>
                          {prof && <Badge data-testid="admin-profile-badge" className={`${prof.cls} mt-1`}>{prof.label}</Badge>}
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-slate-500">
                        Affidabilità: <span className="font-mono2 font-semibold">{sectionB.reliability.toFixed(2)}</span> (soglia 3.00) ·
                        Desiderabilità sociale: <span className="font-mono2">{sectionB.f_mean.toFixed(2)}</span> ·
                        Dev. standard: <span className="font-mono2">{sectionB.stdev.toFixed(2)}</span>
                      </p>
                      {sectionB.alerts?.length > 0 && (
                        <div className="mt-4 border-t border-slate-100 pt-3" data-testid="admin-alerts">
                          <p className="eyebrow mb-2 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> Indicatori di allerta</p>
                          <div className="flex flex-wrap gap-2">
                            {sectionB.alerts.map((al) => (
                              <Badge key={al} className="bg-amber-100 text-amber-800">{ALERT_LABELS[al] || al}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="mt-4 text-sm text-slate-500">Sezione B non svolta.</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {radarData.length > 0 && (
              <Card className="institutional-card">
                <CardContent className="p-6">
                  <p className="eyebrow mb-2">Radar profilo attitudinale (medie per area, scala 1-5)</p>
                  <div data-testid="admin-radar-chart" className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData} outerRadius="72%">
                        <PolarGrid stroke="#CBD5E1" />
                        <PolarAngleAxis dataKey="subject" tick={RADAR_ANGLE_TICK} />
                        <PolarRadiusAxis domain={RADAR_DOMAIN} tick={RADAR_RADIUS_TICK} axisLine={false} />
                        <Radar dataKey="score" stroke="#0284C7" fill="rgba(2,132,199,0.25)" fillOpacity={1} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {isNewInstrument && sectionB && adminProfile && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="institutional-card">
                    <CardContent className="p-6">
                      <p className="eyebrow mb-2">Profilo motivazionale orientativo</p>
                      <MotivationalProfile profile={adminProfile} />
                    </CardContent>
                  </Card>
                  <Card className="institutional-card">
                    <CardContent className="p-6">
                      <p className="eyebrow mb-4">Preferenze e approfondimenti (O1-O4)</p>
                      <div className="space-y-4" data-testid="open-answers-block">
                        {(sess.preferences_snapshot || data.course_preferences || []).length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-slate-700 mb-1.5">Preferenze dichiarate nella candidatura</p>
                            <div className="flex flex-wrap gap-2" data-testid="interview-preferences">
                              {(sess.preferences_snapshot || data.course_preferences || []).map((p) => (
                                <Badge key={p.rank} variant="secondary" className="text-xs">{p.rank}ª · {p.course_name}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        <div>
                          <p className="text-xs font-semibold text-slate-700">O1 · Interesse per l'area tecnologica</p>
                          <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap" data-testid="o1-answer">{oa.O1 || "Risposta non fornita"}</p>
                          {osugg.O1 && <p className="mt-1 text-xs italic text-slate-500">Domanda per il colloquio: {osugg.O1}</p>}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-700">O2 · Attività di interesse per corso</p>
                          <div className="mt-1 space-y-2">
                            {(oa.O2 || []).map((row) => (
                              <div key={row.rank} className="text-sm" data-testid={`o2-answer-${row.rank}`}>
                                <p className="text-xs text-slate-500">{row.rank}ª preferenza · {row.course_name}</p>
                                <p className="text-slate-600 whitespace-pre-wrap">{row.unknown_course ? "Non conosce ancora abbastanza questo corso" : (row.text || "Risposta non fornita")}</p>
                                {(osugg.O2 || []).filter((x) => x.rank === row.rank).map((x) => (
                                  <p key={x.rank} className="text-xs italic text-slate-500">Domanda per il colloquio: {x.suggestion}</p>
                                ))}
                              </div>
                            ))}
                            {(oa.O2 || []).length === 0 && <p className="text-sm text-slate-500">Risposta non fornita</p>}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-700">O3 · Ordine delle preferenze</p>
                          <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap" data-testid="o3-answer">
                            {oa.O3?.exploring ? "La mia scelta è ancora da chiarire" : (oa.O3?.text || "Risposta non fornita")}
                          </p>
                          {osugg.O3 && <p className="mt-1 text-xs italic text-slate-500">Domanda per il colloquio: {osugg.O3}</p>}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-700">O4 · Difficoltà affrontata</p>
                          <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap" data-testid="o4-answer">
                            {oa.O4?.no_example ? "Non ho un esempio da riportare" : (oa.O4?.text || "Risposta non fornita")}
                          </p>
                          {osugg.O4 && <p className="mt-1 text-xs italic text-slate-500">Domanda per il colloquio: {osugg.O4}</p>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <InterviewPanel appId={id} rubric={data.interview_rubric || []} interview={data.interview}
                  history={data.interview_history || []} overall={data.overall_score} spunti={sess.spunti || []} onSaved={load} />
              </>
            )}

            {isRotta && sectionB && (
              <>
                <RottaPanel sess={sess} appId={id} invites={data.update_invites || []} onSaved={load} />
                <InterviewPanel appId={id} rubric={data.interview_rubric || []} interview={data.interview}
                  history={data.interview_history || []} overall={data.overall_score} spunti={[]} onSaved={load} />
              </>
            )}

            {(sess.section_a_answers?.length > 0 || sess.section_b_answers?.length > 0) && (
              <Card className="institutional-card">
                <CardContent className="p-6">
                  <p className="eyebrow mb-4">Risposte date dal candidato</p>
                  <Accordion type="multiple" data-testid="admin-answers-accordion">
                    {sess.section_a_answers?.length > 0 && (
                      <AccordionItem value="sezione-a">
                        <AccordionTrigger data-testid="answers-section-a-toggle">
                          Sezione A · {sess.section_a_answers.length} domande ({sess.section_a_total ?? 0} punti)
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-4" data-testid="answers-section-a-list">
                            {sess.section_a_answers.map((ans, i) => (
                              <div key={ans.question_id} className="border border-slate-100 rounded-lg p-4">
                                <div className="flex items-start gap-2">
                                  <AnswerIcon selected={ans.selected} correct={ans.correct_index} />
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-slate-900">
                                      <span className="font-mono2 text-slate-400 mr-2">D{i + 1}</span>{ans.text}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-0.5">{ans.category} · {ans.points} pt</p>
                                    <ul className="mt-2 space-y-1">
                                      {ans.options.map((opt, oi) => (
                                        <li key={`${ans.question_id}-${oi}`} className={`text-xs px-2 py-1 rounded ${
                                          oi === ans.correct_index ? "bg-green-50 text-green-800 font-medium" :
                                          oi === ans.selected ? "bg-red-50 text-red-700" : "text-slate-600"
                                        }`}>
                                          {oi === ans.selected ? "→ " : ""}{opt}
                                          {oi === ans.correct_index ? " (corretta)" : ""}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    )}
                    {sess.section_b_answers?.length > 0 && (
                      <AccordionItem value="sezione-b">
                        <AccordionTrigger data-testid="answers-section-b-toggle">
                          Sezione B · {sess.section_b_answers.length} item (scala 1-5)
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-1.5" data-testid="answers-section-b-list">
                            {[...sess.section_b_answers].sort((a, b) => a.code.localeCompare(b.code)).map((ans) => (
                              <div key={ans.code} className="flex items-center justify-between gap-4 text-xs border-b border-slate-50 py-1.5">
                                <span className="text-slate-700"><span className="font-mono2 text-slate-400 mr-2">{ans.code}</span>{ans.text}</span>
                                <Badge variant="secondary" className="font-mono2 shrink-0">{ans.value}/5</Badge>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    )}
                  </Accordion>
                </CardContent>
              </Card>
            )}

            {sectionB?.validation_questions?.length > 0 && (
              <Card className="institutional-card border-l-4 border-l-amber-400">
                <CardContent className="p-6">
                  <p className="eyebrow mb-3 flex items-center gap-2"><HelpCircle className="h-4 w-4 text-amber-600" /> Domande di validazione per il colloquio (promemoria esaminatore)</p>
                  <ul className="space-y-2" data-testid="admin-validation-questions">
                    {sectionB.validation_questions.map((q, i) => (
                      <li key={q} className="text-sm text-slate-700 leading-relaxed flex gap-2">
                        <span className="font-mono2 text-amber-600 shrink-0">{i + 1}.</span> {q}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="institutional-card">
                <CardContent className="p-6">
                  <p className="eyebrow mb-4 flex items-center gap-2"><ScrollText className="h-4 w-4" /> Log di sessione</p>
                  <div className="max-h-80 overflow-y-auto space-y-1.5" data-testid="admin-session-logs">
                    {(sess.logs || []).map((l, i) => (
                      <div key={`${l.at}-${l.event}`} className="flex items-baseline gap-3 text-xs">
                        <span className="font-mono2 text-slate-400 shrink-0">{new Date(l.at).toLocaleTimeString("it-IT")}</span>
                        <span className="font-mono2 font-semibold text-[#003366] shrink-0">{l.event}</span>
                        <span className="text-slate-500 truncate">{l.detail}</span>
                      </div>
                    ))}
                    {(sess.logs || []).length === 0 && <p className="text-sm text-slate-500">Nessun evento registrato.</p>}
                  </div>
                </CardContent>
              </Card>

              <Card className="institutional-card">
                <CardContent className="p-6">
                  <p className="eyebrow mb-4">Snapshot proctoring ({(sess.snapshots || []).length})</p>
                  <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto" data-testid="admin-snapshots-grid">
                    {(sess.snapshots || []).map((s, i) => (
                      <a key={s.path} href={`${API}/files/${s.path}`} target="_blank" rel="noreferrer" data-testid={`snapshot-thumb-${i}`}>
                        <img src={`${API}/files/${s.path}`} alt={`Snapshot ${i + 1}`} className="rounded border border-slate-200 object-cover w-full h-20" />
                      </a>
                    ))}
                    {(sess.snapshots || []).length === 0 && <p className="text-sm text-slate-500 col-span-3">Nessuno snapshot acquisito.</p>}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
